import { Button } from '../../interfaces/controller';

/** Gamepad-side sink: accepts a full 8-bit button mask each frame. */
export interface GamepadMaskSink {
    setGamepadMask(mask: number): void;
}

/**
 * Gamepad API adapter. Poll with update() once per rendered frame.
 *
 * Standard-mapping pad layout (https://w3c.github.io/gamepad/#remapping):
 *   buttons[0]  (cross / A)      -> A
 *   buttons[1]  (circle / B)     -> B
 *   buttons[8]  (share/select)   -> Select
 *   buttons[9]  (start)          -> Start
 *   buttons[12..15] (d-pad)      -> Up/Down/Left/Right
 *   axes[0]/axes[1] (left stick) -> d-pad (with dead zone)
 *
 * Browser-only module; imported solely by src/index.ts.
 */
export class GamepadAdapter {
    private sink: GamepadMaskSink;
    private padIndex: number | null = null;
    private deadZone = 0.4;

    constructor(sink: GamepadMaskSink) {
        this.sink = sink;
    }

    private findPad(): Gamepad | null {
        if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') {
            return null;
        }
        const pads = navigator.getGamepads();
        if (this.padIndex !== null) {
            const p = pads[this.padIndex];
            if (p && p.connected) return p;
            this.padIndex = null;
        }
        for (const p of pads) {
            if (p && p.connected) {
                this.padIndex = p.index;
                return p;
            }
        }
        return null;
    }

    /** Poll the first connected gamepad and push its state to the sink. */
    update(): void {
        const pad = this.findPad();
        if (!pad) {
            this.sink.setGamepadMask(0);
            return;
        }
        const b = (i: number) => !!(pad.buttons[i] && pad.buttons[i].pressed);
        const axisX = pad.axes[0] || 0;
        const axisY = pad.axes[1] || 0;

        let mask = 0;
        const set = (button: Button, pressed: boolean) => { if (pressed) mask |= 1 << button; };
        set(Button.A, b(0));
        set(Button.B, b(1));
        set(Button.Select, b(8));
        set(Button.Start, b(9));
        set(Button.Up, b(12) || axisY < -this.deadZone);
        set(Button.Down, b(13) || axisY > this.deadZone);
        set(Button.Left, b(14) || axisX < -this.deadZone);
        set(Button.Right, b(15) || axisX > this.deadZone);
        this.sink.setGamepadMask(mask);
    }
}
